import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {customFetcher} from "./customFetcher";
import {drinkSchema, IDrink} from "../Interfaces/IDrink";

const DRINK_QUERY_KEY = ['drink'];

export const useGetDrinks = ({ includeDeleted = false }: { includeDeleted?: boolean } = {}) => {
    const { data: drinkArray, isLoading: isLoadingDrinkArray } = useQuery<IDrink[]>({
        refetchOnWindowFocus: false,
        queryKey: [...DRINK_QUERY_KEY, includeDeleted],
        queryFn: async () => {
            const data = await customFetcher<unknown[]>({
                endpoint: `/drinks/${includeDeleted ? '?include_deleted=true' : ''}`,
            });
            return drinkSchema.array().parse(data);
        },
        initialData: [],
    });
    return { drinkArray: drinkArray as IDrink[], isLoadingDrinkArray: isLoadingDrinkArray };
};

export const useCreateDrink = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: createDrink, isPending: isCreatingDrink } = useMutation<IDrink, Error, Omit<IDrink, 'id'>>({
        mutationFn: (newDrink) =>
            customFetcher<IDrink>({
                endpoint: '/drinks/',
                method: 'POST',
                body: newDrink,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: DRINK_QUERY_KEY });
        },
    });
    return { createDrink, isCreatingDrink };
};

export const useUpdateDrink = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateDrink, isPending: isUpdatingDrink } = useMutation<IDrink, Error, IDrink>({
        mutationFn: (updatedDrink: IDrink) =>
            customFetcher<IDrink>({
                endpoint: `/drinks/${updatedDrink.id}/`,
                method: 'PUT',
                body: updatedDrink,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: DRINK_QUERY_KEY });
        },
    });
    return { updateDrink: updateDrink, isUpdatingDrink: isUpdatingDrink };
};

export const useDeleteDrink = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: deleteDrink, isPending: isDeletingDrink } = useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await customFetcher({
                endpoint: `/drinks/${id}/`,
                method: 'DELETE',
            });
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: DRINK_QUERY_KEY });
        },
    });
    return { deleteDrink, isDeletingDrink };
};
